import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PAT } from "@/constants/testIds";
import {
  actorDisplay,
  appointmentStatusLabel,
  appointmentStatusTone,
  appointmentVisitType,
  confirmationStatusLabel,
  formatAppointmentTime,
  providerDisplay,
  referringProviderDisplay,
} from "@/lib/appointmentApi";
import { encounterNumber, formatDate } from "@/lib/patientChart";

const STATUS_STYLE = {
  booked: "bg-sky-100 text-sky-800",
  arrived: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-slate-200 text-slate-600",
  noshow: "bg-red-100 text-red-700",
};

const CONFIRM_STYLE = {
  confirmed: "bg-emerald-100 text-emerald-800",
  pending: "bg-orange-100 text-orange-800",
  unconfirmed: "bg-slate-100 text-slate-700",
};

const STATUS_BADGE = "emr-chart-dense__badge rounded-none border-0";

function appointmentTimeLabel(appointment) {
  const time = formatAppointmentTime(appointment.start);
  return appointment.minutesDuration ? `${time} · ${appointment.minutesDuration} min` : time;
}

function openEncounterFromRow(event, appointment, onOpenEncounter) {
  event.stopPropagation();
  onOpenEncounter(appointment);
}

export default function AppointmentTable({
  appointments,
  encounterById = {},
  onOpen,
  onOpenEncounter,
  canAccessEncounters = true,
}) {
  return (
    <div className="emr-chart-table-wrap">
      <Table className="emr-chart-appt-table">
        <TableHeader>
          <TableRow className="bg-slate-50 hover:bg-slate-50">
            <TableHead>Date</TableHead>
            <TableHead>Time</TableHead>
            <TableHead>Visit Type</TableHead>
            <TableHead>Provider</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Referring Provider</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Encounter</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {appointments.map((appointment) => {
            const encounterLabel = appointment.encounterId
              ? encounterNumber(encounterById[appointment.encounterId])
              : "";
            return (
              <TableRow
                key={appointment.id}
                data-testid={`${PAT.chartAppointmentRow}-${appointment.id}`}
                className="cursor-pointer"
                onClick={() => onOpen?.(appointment)}
              >
                <TableCell className="text-slate-700">{formatDate(appointment.start)}</TableCell>
                <TableCell className="font-medium text-slate-800">{appointmentTimeLabel(appointment)}</TableCell>
                <TableCell className="text-slate-700">{appointmentVisitType(appointment) || "—"}</TableCell>
                <TableCell className="text-slate-700">{providerDisplay(appointment) || "—"}</TableCell>
                <TableCell className="text-slate-700">{actorDisplay(appointment, "Location/") || "—"}</TableCell>
                <TableCell className="text-slate-700">{referringProviderDisplay(appointment) || "—"}</TableCell>
                <TableCell>
                  <Badge className={`${STATUS_BADGE} ${STATUS_STYLE[appointmentStatusTone(appointment.status)] || "bg-slate-100 text-slate-700"} hover:opacity-100`}>
                    {appointment.statusLabel || appointmentStatusLabel(appointment.status)}
                  </Badge>
                  {" "}
                  <Badge className={`${STATUS_BADGE} ${CONFIRM_STYLE[appointment.confirmationStatus] || "bg-slate-100 text-slate-600"} hover:opacity-100`}>
                    {confirmationStatusLabel(appointment.confirmationStatus)}
                  </Badge>
                </TableCell>
                <TableCell>
                  {appointment.encounterId && canAccessEncounters ? (
                    <span
                      role="link"
                      tabIndex={0}
                      className="font-medium text-[#1E79E8] hover:underline"
                      onClick={(event) => openEncounterFromRow(event, appointment, onOpenEncounter)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openEncounterFromRow(event, appointment, onOpenEncounter);
                        }
                      }}
                    >
                      {encounterLabel ? `${encounterLabel} · Open Encounter` : "Open Encounter"}
                    </span>
                  ) : (
                    <span className="text-slate-700">—</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
